import { attributeInt } from "../helpers";

const updateDelay    = 30;
const transitionTime = 2000;

const collectSlides = slides => slides.filter(
    slide => slide && slide.classList.contains('carousel-slide')
);

const getMaxHeight = elements => elements.reduce(
    (v, e) => Math.max(v, e.offsetHeight), 0
);

const getBrowserVisibilityAPIDetail = () =>
{
    if( typeof document.hidden !== 'undefined' )
        return { event: 'visibilitychange',       state: 'hidden' };
    else if( typeof document.msHidden !== 'undefined' )
        return { event: 'msvisiblitychange',      state: 'msHidden' };
    else if( typeof document.webkitHidden !== 'undefined' )
        return { event: 'webkitvisibilitychange', state: 'webkitHidden' };

    return false;
};

export const carouselComponentData = {
    booted  : false,
    pointer : 0,
    slides  : [],

    container_height    : 0,
    interaction_blocked : false,

    slide_change_interval : 0,
    slide_change_timer    : null,
    slide_change_active   : false
};

export const carouselComponentComputed = {
    containerHeight : function() {
        return this.container_height
            ? this.container_height + 'px'
            : null
        ;
    },
    isBooted : function() {
        return this.booted;
    }
};

export const carouselComponentMethods = {
    boot : function() {
        this.booted = true;
    },
    bindEventListeners : function() {
        window.addEventListener( 'resize', this.calculateContainerSize );

        const visibilityApiDetail = getBrowserVisibilityAPIDetail();
        if( visibilityApiDetail ) {
            document.addEventListener( visibilityApiDetail.event, () => {
                document[ visibilityApiDetail.state ]
                    ? this.stopAutoChangeTimer()
                    : this.startAutoChangeTimer()
                ;
            });
        }

        const controls = this.$el.querySelectorAll('[data-carousel-pause]');
        if( controls && controls.length ) {
            controls.forEach( control => {
                control.addEventListener( 'mouseenter', this.onMouseEnter );
                control.addEventListener( 'mouseleave', this.onMouseLeave );
            });
        }
    },
    calculateContainerSize : function() {
        this.container_height = getMaxHeight( this.slides );
    },
    changeSlides : function( next_index, stage_class, outgoing_class ) {
        this.prepareForTransition();
        let incoming = this.stageIncoming( next_index, stage_class );
        let outgoing = this.getActiveSlide();

        setTimeout( () => {
            this.preventInteractionFor( transitionTime + updateDelay );
            this.transition( incoming, outgoing, outgoing_class );
            this.pointer = this.getIndex( next_index );
        }, updateDelay );
    },
    getActiveSlide : function() {
        return this.getSlideForIndex( this.pointer );
    },
    getIndex : function( index ) {
        while( this.slides.length && index < 0 )
            index += this.slides.length;
        return index % this.slides.length;
    },
    getSlideForIndex : function( index ) {
        return this.slides[ this.getIndex( index ) ];
    },
    onMouseEnter : function() {
        this.stopAutoChangeTimer();
    },
    onMouseLeave : function() {
        this.startAutoChangeTimer();
    },
    prepareForTransition : function() {
        this.slides.map( ( e, i ) => ( i !== this.pointer ) && e.classList.remove( 'transition' ) );
    },
    preventInteractionFor : function( duration ) {
        this.interaction_blocked = true;
        setTimeout( () => {
            this.interaction_blocked = false;
        }, duration );
    },
    slideNext : function( event ) {
        event && event.preventDefault();
        if( !this.interaction_blocked ) {
            this.changeSlides( this.pointer + 1, 'offstage-right', 'offstage-left' );
        }
    },
    slidePrevious : function( event ) {
        event && event.preventDefault();
        if( !this.interaction_blocked ) {
            this.changeSlides( this.pointer - 1, 'offstage-left', 'offstage-right' );
        }
    },
    stageIncoming : function( index, stage_class ) {
        let slide = this.getSlideForIndex( index );
        slide.classList.remove( 'transition', 'offstage-left', 'offstage-right' );
        slide.classList.add( stage_class );

        return slide;
    },
    startAutoChangeTimer : function() {
        this.stopAutoChangeTimer();
        if( ! this.slide_change_active )
            return;

        this.slide_change_timer = setTimeout( () => {
            this.slideNext();
            this.startAutoChangeTimer();
        }, this.slide_change_interval );
    },
    stopAutoChangeTimer : function() {
        if( this.slide_change_timer )
            clearTimeout( this.slide_change_timer );
    },
    transition : function( incoming, outgoing, outgoing_class ) {
        /* We need the DOM to update before we can can trigger the transition classes
         * and begin the animation otherwise it will add the transition phase to the
         * staging as one operation, whereas we need to invisibly stage the next slide.
         */
        this.onBeforeTransition( outgoing, incoming );
        setTimeout( () => {
            incoming.classList.add( 'transition' );
            outgoing.classList.add( 'transition' );

            incoming.classList.add( 'active' );
            incoming.classList.remove( 'offstage-left', 'offstage-right' );

            outgoing.classList.add( outgoing_class );
            outgoing.classList.remove( 'active' );
        }, updateDelay );
    },
    unbindEventListeners : function() {
        const root = this.vm.$el;
        root.removeEventListener( 'mouseenter', this.onMouseEnter );
        root.removeEventListener( 'mouseleave', this.onMouseLeave );

        window.removeEventListener( 'resize', this.calculateContainerSize );
        window.removeEventListener( 'focus',  this.startAutoChangeTimer   );
        window.removeEventListener( 'blur',   this.stopAutoChangeTimer    );
    },

    // Hooks for overriding...
    onBeforeMount : function() {},
    onMounted     : function() { /* returning false stops any events being bound */ },
    onBeforeBoot  : function() { /* returning false will abort the boot */ },
    onAfterBooted : function() { /* only called if the component boots */  },
    onBeforeTransition : function( outgoing, incoming ) {}
};


export const carouselComponentDefinition = {
    methods : {
        ...carouselComponentMethods
    },
    beforeMount : function() {
        this.onBeforeMount();
    },
    mounted : function() {
        if( false === this.onMounted() )
            return;

        this.bindEventListeners();

        // Bind the slides into the component...
        let refs    = Object.values( this.$refs );
        this.slides = collectSlides( refs );
        if( this.slides.length < 2 )
            return;

        // We'll aggregate slides and do size calculations _before_ we call
        // the onBeforeBoot hook, so that if we need to know things like slide
        // count, etc, then we're able to use that information in deciding
        // whether to complete the bootup...

        // And show the active one.
        this.slides.map((e, i) => e.classList.add(i ? 'offstage-right' : 'active'));
        // Size the slide container...
        this.calculateContainerSize();

        const autochange_attribute = attributeInt( this.$el, 'data-autochange' );
        if( autochange_attribute && autochange_attribute >= 5 ) {
            this.slide_change_interval = autochange_attribute * 1000;
            this.slide_change_active   = true;
        }

        if( false !== this.onBeforeBoot() ) {
            // There's probably a better way of doing this, but
            // this will suffice for now. It's basically just to
            // catch the page reflowing because the icons or font
            // loaded.
            [100, 500, 1000, 2000, 5000].map( time => setTimeout(
                () => this.calculateContainerSize(), time
            ));

            this.startAutoChangeTimer();

            this.boot();
            this.onAfterBooted();
        }
    },
    // ...and unsubscribe.
    destroyed() {
        this.unbindEventListeners();
    },


};
